/**
 * ================================================================================================
 *  שינוי תכנית — THE OFFICE MOVES ITSELF, AND IS TOLD EXACTLY WHAT WILL HAPPEN
 * ================================================================================================
 *
 * Item 4 of the Owner's accumulated list: *"שדרוג/הורדה בשירות עצמי."*
 *
 * And the sentence that decides who may NOT, which this panel renders rather than hides:
 *
 *     *"לקוח בעל תנאי מסחרי מיוחד — מסכים למסלול בקשה כאשר השינוי אינו ניתן לביצוע תוך שימור
 *      התנאים שלו. אין לבטל או להחליף תנאי מוסכם בשקט."*
 *
 * ── THE THREE ANSWERS THIS PANEL HAS TO TELL APART ────────────────────────────────────────────
 *
 *   AN UPGRADE HAPPENED NOW. `changed: true` with no date — the limits are already higher.
 *   A DOWNGRADE IS BOOKED. `changed: true` WITH `downgradeEffectiveFrom`, which is the day it
 *     will happen and not a day somebody must come back. 08 §5.3: *"at period end only"*, so the
 *     office keeps the plan it is paying for until the boundary.
 *   IT WAS REFUSED, and WHICH refusal decides what the customer can do about it: a downgrade
 *     below current usage names the dimensions to reduce; a negotiated term names the request
 *     path; anything else is reported by its own word.
 *
 * A PANEL THAT SAID *"הבקשה בוצעה"* FOR THE FIRST TWO would tell an office its lower price is in
 * force while it is still paying the higher one for a fortnight.
 *
 * ── IT FORMATS NO MONEY AND JUDGES NOTHING ────────────────────────────────────────────────────
 *
 * The prices are `formatAmount`'s own strings, server-side. Whether a move is an upgrade or a
 * downgrade, whether it is allowed, and when it takes effect are all the SERVER's answers — a
 * screen that pre-judged them would be a second copy of §5.3, and the copy would be the one in
 * front of the customer when they pressed.
 */

import { useCallback, useState } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { Modal } from './Modal';
import { ServerRead } from './ServerRead';
import { StateBlock } from './StateBlock';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';
import { routes } from '../routes';
import { tenantLifecycleRefusalText } from '../api/lifecycleText';
import { useServerData } from '../api/useServerData';
import type { PlanOfferView } from '../api/contract';

/**
 * WHAT THE PANEL HOLDS AFTER A PRESS: the server MOVED something, or it REFUSED.
 *
 * TWO SHAPES AND NOT ONE WITH A FLAG, because they arrive through two different paths -- a 200
 * body and a 409 the error taxonomy classifies -- and a single shape would have a `changed` field
 * that is false on one of them for a reason nobody set.
 */
type Outcome =
  | { readonly kind: 'moved'; readonly downgradeEffectiveFrom?: string; readonly supersededSchedule?: boolean }
  | {
      readonly kind: 'refused';
      readonly reason: string;
      readonly arcOwner?: string;
      readonly limitsOverUsage?: readonly {
        readonly limitCode: string;
        readonly limit: number;
        readonly current: number;
      }[];
    };

/** One dimension the office is over, as the server sends it. */
interface OverUsage {
  readonly limitCode: string;
  readonly limit: number;
  readonly current: number;
}

/**
 * The fields `refuse()` was asked to carry beyond the reason, read DEFENSIVELY off JSON.
 *
 * EVERY ENTRY IS SHAPE-CHECKED rather than cast. A first draft cast the array through a
 * conditional type, which compiled, resolved to a type that accepted nothing useful, and rendered
 * an EMPTY list under the words *"יש להקטין קודם את מה שמעל המגבלה"* — a customer told to reduce
 * something, and not told what.
 */
function overUsageOf(value: unknown): readonly OverUsage[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rows: OverUsage[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const row = entry as Record<string, unknown>;
    if (
      typeof row['limitCode'] === 'string' &&
      typeof row['limit'] === 'number' &&
      typeof row['current'] === 'number'
    ) {
      rows.push({ limitCode: row['limitCode'], limit: row['limit'], current: row['current'] });
    }
  }
  return rows.length === 0 ? undefined : rows;
}

function refusalOf(body: unknown): Outcome | null {
  if (typeof body !== 'object' || body === null) return null;
  const row = body as Record<string, unknown>;
  if (typeof row['error'] !== 'string') return null;
  const over = overUsageOf(row['limitsOverUsage']);
  return {
    kind: 'refused',
    reason: row['error'],
    ...(typeof row['arcOwner'] === 'string' ? { arcOwner: row['arcOwner'] } : {}),
    ...(over === undefined ? {} : { limitsOverUsage: over }),
  };
}


export function PlanChangePanel({ onChanged }: { readonly onChanged: () => void }): JSX.Element {
  const read = useCallback(() => callOperation('tenant.plan.offers.read', {}), []);
  const offers = useServerData(read);

  const [chosen, setChosen] = useState<PlanOfferView | null>(null);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  /*
   * ── THE OUTCOME IS HELD HERE, AND THE PANEL MUST NOT BE UNMOUNTED BY `onChanged` ─────────
   *
   * `onChanged` tells the parent to re-read whatever it shows about the subscription. A panel
   * mounted INSIDE that read is unmounted while it reloads, and this state goes with it — the
   * office changes its plan and the screen says nothing.
   *
   * THE FIRST DRAFT OF THIS COMMENT CLAIMED THE STATE SURVIVED, and the component was mounted
   * inside exactly such a read. It did not survive; the browser showed it on the first press,
   * and the dom suite could not, because it mounts this panel alone. A comment asserting a
   * property is not the property — `TenantSubscriptionScreen` is where the placement lives and
   * where that claim now is.
   */
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  async function change(planCode: string): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      const result = await callOperation('tenant.subscription.plan.change', { body: { planCode } });
      setOutcome({
        kind: 'moved',
        ...(result.downgradeEffectiveFrom === undefined
          ? {}
          : { downgradeEffectiveFrom: result.downgradeEffectiveFrom }),
        ...(result.supersededSchedule === undefined
          ? {}
          : { supersededSchedule: result.supersededSchedule }),
      });
      onChanged();
    } catch (err) {
      /*
       * ── A 409 IS A REFUSAL AND NOT A FAULT, AND THE PRODUCT ALREADY KNOWS THE DIFFERENCE ────
       *
       * `classifyActionError` is the ONE place that decides what kind of failure an exception is,
       * and a second decision procedure here would be a second answer to the same question --
       * which is how a refusal starts reading as an outage on one screen. A `conflict` carries the
       * server's own body; anything else is a fault and goes to the notice.
       */
      const classified = classifyActionError(err);
      const refusal = classified.kind === 'conflict' ? refusalOf(classified.body) : null;
      if (refusal !== null) setOutcome(refusal);
      else setFailure(classified);
    } finally {
      setSending(false);
      setChosen(null);
    }
  }

  return (
    <section className="notice" data-area="plan-change">
      <h2 className="notice__title">שינוי תכנית</h2>
      {failure === null ? null : <ActionFailureNotice failure={failure} />}
      {outcome === null ? null : <PlanChangeOutcome outcome={outcome} />}

      <ServerRead result={offers.result} whatHe="את התכניות המוצעות" skeletonRows={3}>
        {(data) =>
          !data.priceBookAvailable ? (
            /*
             * NOT AN EMPTY LIST. *"אין תכניות"* would say the product has nothing to sell; this
             * says the estate has no published price book, which is somebody's to fix.
             */
            <StateBlock
              titleHe="לא ניתן להציג תכניות"
              bodyHe="אין ספר מחירים מפורסם במערכת, ולכן לא ניתן להציג מחירים."
              tone="unavailable"
            />
          ) : data.offers.length === 0 ? (
            <p className="notice__body">אין כרגע תכניות שניתן לעבור אליהן בשירות עצמי.</p>
          ) : (
            <ul className="list" data-area="plan-offers">
              {data.offers.map((offer) => (
                <li key={offer.planCode} className="list__row">
                  <div className="list__main">
                    <h3 className="list__title">{offer.displayNameHe}</h3>
                    {/*
                      THE SAME `dir`-ON-A-BLOCK DEFECT, IN THE SECOND COPY OF THIS RENDERER.
                      **2026-11-04.** `PublicSignupScreen.PlanOffer` draws the same offer shape;
                      both carried `dir="ltr"` on the paragraph and both threw the figure to the
                      far edge. Fixed in both, and recorded so the next change to one is looked
                      for in the other.
                    */}
                    <p className="list__meta list__meta--lead">
                      {/* THE SERVER'S OWN STRING — this screen formats no money. */}
                      <bdi>{offer.totalDisplay ?? offer.priceDisplay ?? '—'}</bdi>
                    </p>
                    {offer.totalDisplay === null ? null : (
                      <p className="list__meta">
                        {`לפני מע״מ ${offer.priceDisplay ?? ''} · מע״מ ${offer.vatRatePct ?? ''}% ${offer.vatDisplay ?? ''}`}
                      </p>
                    )}
                  </div>
                  {offer.current ? (
                    <p className="list__meta" data-area="plan-current">
                      התכנית הנוכחית
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      name={`plan-${offer.planCode}`}
                      disabled={sending}
                      onClick={() => setChosen(offer)}
                    >
                      מעבר לתכנית הזאת
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </ServerRead>

      {chosen === null ? null : (
        <Modal
          intentHe="שינוי תכנית"
          /*
           * 09 §2.2. THE SENTENCE SAYS WHAT IS NOT DECIDED HERE, and that is the honest part: this
           * screen does not know whether the move is an upgrade or a downgrade — the server
           * compares the two plans' limits — so it must not promise a date it cannot compute.
           * What it CAN say is the figure, and that it will be told which of the two happened.
           */
          blastRadiusHe={
            `הארגון יעבור לתכנית ${chosen.displayNameHe}, במחיר ${chosen.totalDisplay ?? chosen.priceDisplay ?? ''} לתקופה. ` +
            'שדרוג נכנס לתוקף מיד; מעבר לתכנית קטנה יותר נרשם לסוף תקופת החיוב הנוכחית, והארגון ממשיך בתכנית ' +
            'שעליה הוא משלם עד אז. לא מתבצע חיוב ברגע זה.'
          }
          fields={0}
          irreversible={false}
          confirmLabelHe={sending ? 'משנה…' : 'אישור המעבר'}
          onConfirm={() => void change(chosen.planCode)}
          onClose={() => setChosen(null)}
        >
          {/*
            THE CURRENT PLAN BESIDE THE NEW ONE. A confirmation that named only the destination
            would ask somebody to approve a change without the figure they are changing FROM.
          */}
          <p className="form__note">{`התכנית הנבחרת: ${chosen.displayNameHe}`}</p>
        </Modal>
      )}
    </section>
  );
}

/** The three answers, told apart — see the module header for why that is the whole job. */
function PlanChangeOutcome({ outcome }: { readonly outcome: Outcome }): JSX.Element {
  if (outcome.kind === 'moved' && outcome.downgradeEffectiveFrom !== undefined) {
    return (
      <StateBlock
        titleHe="המעבר נרשם לסוף התקופה"
        bodyHe={`הארגון יעבור לתכנית החדשה ב־${outcome.downgradeEffectiveFrom}. עד אז ממשיכה התכנית הנוכחית, והחיוב לפיה.`}
        nextStep={
          outcome.supersededSchedule === true ? (
            <p>מעבר שנרשם קודם לכן בוטל, ורק המעבר הזה יתבצע.</p>
          ) : undefined
        }
      />
    );
  }
  if (outcome.kind === 'moved') {
    return (
      <StateBlock
        titleHe="התכנית שונתה"
        bodyHe="המגבלות החדשות בתוקף מהבקשה הבאה. החיוב לתקופה הנוכחית מחושב לפי הימים בכל תכנית."
      />
    );
  }
  if (outcome.reason === 'downgrade_below_usage') {
    /*
     * THE ONE REFUSAL A CUSTOMER CAN ACT ON, so it is rendered with the dimensions and the two
     * figures rather than as a sentence. §5.3: *"the tenant must first reduce companies/users/SKUs
     * below the target plan's limits"* -- told WHAT to reduce and by HOW MUCH.
     */
    return (
      <StateBlock
        titleHe="התכנית הקטנה אינה מספיקה לשימוש הנוכחי"
        bodyHe="כדי לעבור לתכנית הזאת יש להקטין קודם את מה שמעל המגבלה:"
        tone="unavailable"
        nextStep={
          <ul className="notice__list" data-area="plan-over-usage">
            {(outcome.limitsOverUsage ?? []).map((line) => (
              <li key={line.limitCode}>
                {`${line.limitCode}: ${String(line.current)} בשימוש, המגבלה בתכנית היא ${String(line.limit)}`}
              </li>
            ))}
          </ul>
        }
      />
    );
  }
  /*
   * ── THE WORDS COME FROM `lifecycleText`, WHICH IS THE PRODUCT'S ONE REFUSAL MAP ────────────
   *
   * A map of its own here would be a second copy, and `contract.mirror.test.ts` sweeps the
   * SERVER's reasons against that one -- so a reason named only here would pass as "nothing
   * silent" while this panel was the only place it existed.
   *
   * `currentStatus` AND `permittedFrom` ARE EMPTY ON PURPOSE: this refusal is about a plan and
   * not about a lifecycle state, so there is no state to report and no list of origins. The
   * mapper's own rule then applies -- a row's declared next step outranks the derived trailer,
   * and both of item 4's refusals declare one, because a refresh answers them the same way.
   */
  const copy = tenantLifecycleRefusalText({
    reason: outcome.reason,
    currentStatus: null,
    permittedFrom: [],
  });
  return (
    <StateBlock
      titleHe={copy.titleHe}
      bodyHe={copy.explanationHe}
      tone="unavailable"
      nextStep={
        <>
          <p>{copy.nextStepHe}</p>
          {outcome.arcOwner === 'billing.change.request' ? (
            <p>
              <a href={routes.tenantBilling}>למסלול בקשת שינוי</a>
            </p>
          ) : null}
        </>
      }
    />
  );
}
